/**
 * cart-thumbnail-upload.js — Shared helper for uploading the customer's
 * client-rendered cart thumbnail to skylit-admin's R2 store.
 *
 * Why this exists:
 *   The /api/cart-thumbnail/ endpoint caps payloads at 2MB (binary). On
 *   high-DPI canvases or large mockup composites the storefront-generated
 *   data URL can exceed that and 413, leaving no R2 entry — which then
 *   cascades into broken cart thumbnails on stale sessions that have lost
 *   localStorage.
 *
 *   This helper auto-shrinks the data URL before upload until it fits
 *   comfortably under the cap, so every customizer's add-to-cart flow can
 *   rely on the backend copy as a fallback.
 *
 * Usage:
 *   window.skylitUploadCartThumbnail({
 *     order: state.orderId,
 *     productType: 'star',          // 'star' | 'moon' | 'hor_moon' | 'location_map' | 'custom_print'
 *     dataUrl: thumb                // base64 PNG/JPEG data URL
 *   });
 *
 * Always fire-and-forget — never blocks add-to-cart.
 */
(function() {
  if (window.skylitUploadCartThumbnail) return;

  var ADMIN_BASE = 'https://admin.skylitstudio.co.uk/';
  var MAX_BYTES = 1800 * 1024; // 1.8MB, leaves headroom for the JSON envelope

  // Per-product `innerRange` floor for validateThumbnail. Moon prints at
  // silver/grey texture against a dark starry-sky bg have a much narrower
  // luminance range inside the frame cutout than a star map (which gets
  // many bright pixels from individual stars). 18 was tuned for star maps
  // and was rejecting legitimate moon thumbnails — see cf5wjm8sqy audit.
  // 10 still catches the "moon canvas didn't render" failure (pure black
  // inner area is ~0) without false-rejecting a valid moon thumbnail.
  var INNER_RANGE_FLOOR = {
    star:         18,
    moon:         10,
    hor_moon:     10,
    location_map: 18,
    custom_print: 18
  };
  function innerRangeFloor(productType) {
    return INNER_RANGE_FLOOR[productType] != null ? INNER_RANGE_FLOOR[productType] : 18;
  }

  // Fire-and-forget skip-reason beacon. Sends to
  // /api/cart-thumbnail-skip-log/ on admin. Uses sendBeacon when available
  // so the POST survives page unload (the customer often navigates away
  // from the customizer the instant add-to-cart resolves). Falls back to
  // fetch with keepalive.
  function beaconSkip(order, productType, reason, extra) {
    try {
      if (!order || !reason) return;
      var url = ADMIN_BASE + 'api/cart-thumbnail-skip-log/';
      var body = JSON.stringify({
        order: order,
        productType: productType || null,
        reason: reason,
        extra: extra || null
      });
      if (navigator && typeof navigator.sendBeacon === 'function') {
        try {
          var blob = new Blob([body], { type: 'application/json' });
          if (navigator.sendBeacon(url, blob)) return;
        } catch (e) {}
      }
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true
      }).catch(function() {});
    } catch (e) {}
  }
  // Expose so customizer generators (moon-myjs, starmap-v2, etc.) can
  // beacon their own pre-validation failure modes (no_custom_frame,
  // generator_error) without re-implementing the helper.
  window.skylitBeaconThumbnailSkip = beaconSkip;

  function approxBytes(dataUrl) {
    var i = dataUrl.indexOf(',');
    if (i === -1) return dataUrl.length;
    return Math.floor((dataUrl.length - i - 1) * 0.75);
  }

  // Re-encode the data URL into a smaller JPEG until it fits. We resize the
  // canvas (rather than just lowering quality) because resize is the only
  // reliable way to bring a >>2MB thumbnail under the cap — JPEG quality
  // alone caps out around 50-70% reduction.
  function shrinkDataUrl(dataUrl, maxBytes) {
    return new Promise(function(resolve) {
      try {
        if (approxBytes(dataUrl) <= maxBytes) return resolve(dataUrl);
        var img = new Image();
        img.onload = function() {
          var current = dataUrl;
          var w = img.naturalWidth || img.width;
          var h = img.naturalHeight || img.height;
          var quality = 0.85;
          // Up to 4 passes. Each pass halves area (≈ scale = √0.5) and may
          // also drop quality on the last attempt. 4 passes from a 4MB
          // thumbnail comfortably reaches <1MB even on extreme inputs.
          for (var pass = 0; pass < 4; pass++) {
            var ratio = Math.sqrt(maxBytes / approxBytes(current)) * 0.92;
            if (ratio >= 1) break;
            w = Math.max(64, Math.round(w * ratio));
            h = Math.max(64, Math.round(h * ratio));
            var canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, w, h);
            if (pass === 3) quality = 0.7;
            current = canvas.toDataURL('image/jpeg', quality);
            if (approxBytes(current) <= maxBytes) break;
          }
          if (approxBytes(current) > maxBytes) {
            console.warn('[cart-thumb] could not shrink under cap, skipping upload');
            return resolve(null);
          }
          console.log('[cart-thumb] shrank to', w + 'x' + h, '~' + Math.round(approxBytes(current)/1024) + 'KB');
          resolve(current);
        };
        img.onerror = function() { resolve(null); };
        img.src = dataUrl;
      } catch (e) {
        resolve(null);
      }
    });
  }

  // Sanity check: detect a thumbnail where the customer's design failed
  // to render. Two layers:
  //
  //   1. Global range — catches a fully uniform image (pure black, etc.)
  //      where everything failed including the lifestyle mockup overlay.
  //
  //   2. Inner content range — catches the "lifestyle mockup loaded but
  //      the customer's design didn't" case (the realistic failure mode:
  //      the wood frame + wall + dried palms render fine, but inside the
  //      frame is just black with the white inset border line). Samples
  //      only the central content area, excluding the wood frame outside
  //      and the inset border ~10% margin inside the frame cutout, so
  //      the border line doesn't give false range signal.
  //
  //   MOCKUP_FRAME (the fraction of the image occupied by the frame
  //   cutout) is the same constant used by all customizer compositors:
  //   left:0.341, top:0.216, right:0.6425, bottom:0.79. Shrunk by 12%
  //   here to keep the inset border out of the sample.
  function validateThumbnail(dataUrl, opts) {
    var productType = (opts && opts.productType) || null;
    var floor = innerRangeFloor(productType);
    return new Promise(function(resolve) {
      try {
        var img = new Image();
        img.onload = function() {
          try {
            // 256×256 sample so star-map small bright pixels survive the
            // downsample (at 64×64 individual stars get averaged into the
            // surrounding black and disappear).
            var W = 256, H = 256;
            var c = document.createElement('canvas');
            c.width = W; c.height = H;
            var ctx = c.getContext('2d');
            ctx.drawImage(img, 0, 0, W, H);
            var data = ctx.getImageData(0, 0, W, H).data;

            // Inner content area — MOCKUP_FRAME shrunk by 12% on each side
            // so the inset border drawn by the compositor doesn't pollute
            // the inner-range signal.
            var fl = 0.341, ft = 0.216, fr = 0.6425, fb = 0.79;
            var fw = fr - fl, fh = fb - ft;
            var shrink = 0.12;
            var ix0 = Math.floor(W * (fl + fw * shrink));
            var iy0 = Math.floor(H * (ft + fh * shrink));
            var ix1 = Math.floor(W * (fr - fw * shrink));
            var iy1 = Math.floor(H * (fb - fh * shrink));

            var gMin = 999, gMax = -1, iMin = 999, iMax = -1;
            for (var y = 0; y < H; y++) {
              for (var x = 0; x < W; x++) {
                var p = (y * W + x) * 4;
                var lum = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
                if (lum < gMin) gMin = lum;
                if (lum > gMax) gMax = lum;
                if (x >= ix0 && x <= ix1 && y >= iy0 && y <= iy1) {
                  if (lum < iMin) iMin = lum;
                  if (lum > iMax) iMax = lum;
                }
              }
            }
            var globalRange = gMax - gMin;
            var innerRange = iMax - iMin;

            // Pure-uniform whole image → reject.
            if (globalRange < 15) {
              console.warn('[cart-thumb] uniform global image (range=' + Math.round(globalRange) + '), invalid');
              resolve({ ok: false, reason: 'global_range_low', globalRange: globalRange, innerRange: innerRange });
              return;
            }
            // Lifestyle mockup OK but inner design area uniform → reject.
            // Floor is per-product (see INNER_RANGE_FLOOR). Was a flat 18
            // tuned for star maps; moon at silver texture against a dark
            // starry-sky bg legitimately produces ~10–17 inner range and
            // was getting falsely rejected.
            if (innerRange < floor) {
              console.warn('[cart-thumb] uniform inner content area (range=' + Math.round(innerRange) + ', floor=' + floor + ', product=' + (productType || '?') + '), invalid');
              resolve({ ok: false, reason: 'inner_range_low', globalRange: globalRange, innerRange: innerRange, floor: floor });
              return;
            }
            resolve({ ok: true, globalRange: globalRange, innerRange: innerRange });
          } catch (e) {
            // Decoding/sampling failed — fail open (treat as valid so we
            // don't reject perfectly fine thumbnails just because the
            // validator self-crashed).
            resolve({ ok: true, fail_open: true });
          }
        };
        img.onerror = function() { resolve({ ok: true, fail_open: true }); };
        img.src = dataUrl;
      } catch (e) { resolve({ ok: true, fail_open: true }); }
    });
  }

  // Expose the validator on window so cart-side code can use it to
  // self-heal stale localStorage thumbnails (a customer who hit the
  // black-thumbnail bug pre-fix would otherwise keep seeing it on every
  // cart visit until they manually re-saved their design). Backwards-
  // compatible boolean shape — internal callers use the detail object
  // directly; external callers (cart-drawer.js, main-cart-items.liquid)
  // only need the truthy/falsy answer.
  window.skylitValidateThumbnail = function(dataUrl, opts) {
    return validateThumbnail(dataUrl, opts).then(function(result) { return !!(result && result.ok); });
  };

  // Upload-only entry point. Validates first, then shrinks, then POSTs.
  window.skylitUploadCartThumbnail = function(opts) {
    if (!opts || !opts.order || !opts.dataUrl) return Promise.resolve(null);
    var order = opts.order;
    var productType = opts.productType || 'star';
    return validateThumbnail(opts.dataUrl, { productType: productType }).then(function(result) {
      if (!result.ok) {
        console.warn('[cart-thumb] skipping upload for', order, '— failed validation');
        beaconSkip(order, productType, result.reason, {
          globalRange: result.globalRange,
          innerRange: result.innerRange,
          floor: result.floor
        });
        return null;
      }
      return shrinkDataUrl(opts.dataUrl, MAX_BYTES).then(function(finalUrl) {
        if (!finalUrl) {
          beaconSkip(order, productType, 'shrink_failed', { maxBytes: MAX_BYTES });
          return null;
        }
        try {
          return fetch(ADMIN_BASE + 'api/cart-thumbnail/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order: order, productType: productType, dataUrl: finalUrl })
          }).then(function(r) {
            if (!r.ok) {
              beaconSkip(order, productType, 'fetch_failed', { status: r.status });
            }
            return r.json().catch(function() { return null; });
          }).catch(function(err) {
            beaconSkip(order, productType, 'fetch_failed', { error: String(err).slice(0, 200) });
            return null;
          });
        } catch (e) {
          beaconSkip(order, productType, 'fetch_failed', { error: String(e).slice(0, 200) });
          return null;
        }
      });
    });
  };

  // Combined entry point — validates ONCE, then writes to localStorage
  // (so the cart page shows the right thumbnail on the customer's device)
  // AND uploads to R2 (so cross-device sessions can fall back). Skipping
  // both on validation failure means no broken thumbnail enters either
  // surface — cart liquid falls back to the product placeholder, which
  // is strictly better than showing a black square.
  window.skylitStoreCartThumbnail = function(opts) {
    if (!opts || !opts.order || !opts.dataUrl) return Promise.resolve(null);
    var order = opts.order;
    var productType = opts.productType || 'star';
    var dataUrl = opts.dataUrl;
    return validateThumbnail(dataUrl, { productType: productType }).then(function(result) {
      if (!result.ok) {
        console.warn('[cart-thumb] skipping localStorage + upload for', order, '— failed validation');
        beaconSkip(order, productType, result.reason, {
          globalRange: result.globalRange,
          innerRange: result.innerRange,
          floor: result.floor
        });
        return null;
      }
      try { localStorage.setItem('thumbnail_' + order, dataUrl); } catch (e) {}
      return shrinkDataUrl(dataUrl, MAX_BYTES).then(function(finalUrl) {
        if (!finalUrl) {
          beaconSkip(order, productType, 'shrink_failed', { maxBytes: MAX_BYTES });
          return null;
        }
        try {
          return fetch(ADMIN_BASE + 'api/cart-thumbnail/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order: order, productType: productType, dataUrl: finalUrl })
          }).then(function(r) {
            if (!r.ok) {
              beaconSkip(order, productType, 'fetch_failed', { status: r.status });
            }
            return r.json().catch(function() { return null; });
          }).catch(function(err) {
            beaconSkip(order, productType, 'fetch_failed', { error: String(err).slice(0, 200) });
            return null;
          });
        } catch (e) {
          beaconSkip(order, productType, 'fetch_failed', { error: String(e).slice(0, 200) });
          return null;
        }
      });
    });
  };
})();
